// src/pages/commercial/CommercialConfiguratorPage.js
import React, { useState } from 'react';
import pb from '../../lib/pocketbase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// --- DATA ---
const BASES = [
    { id: 'site_vitrine', title: 'Site Vitrine', price: 1500, emoji: '🏢', desc: 'L\'essentiel pour être visible. Design pro & responsive.' },
    { id: 'site_ecommerce', title: 'E-Commerce', price: 3500, emoji: '🛍️', desc: 'Vendez vos produits 24/7. Boutique complète.' },
    { id: 'app_sur_mesure', title: 'App Métier', price: 5000, emoji: '🚀', desc: 'Votre outil de travail sur-mesure. SaaS & Logiciel.' },
];

const OPTIONS = [
    { id: 'seo_boost', title: 'Pack Référencement', price: 500, emoji: '📈', desc: 'Première page Google visée.' },
    { id: 'logo_branding', title: 'Identité Visuelle', price: 800, emoji: '🎨', desc: 'Logo, charte & cartes de visite.' },
    { id: 'maintenance', title: 'Maintenance VIP', price: 1200, emoji: '🛡️', desc: 'Tranquillité totale pendant 1 an.' },
    { id: 'redaction', title: 'Rédaction Pro', price: 400, emoji: '✍️', desc: 'Contenus optimisés pour la conversion.' },
];

export default function CommercialConfiguratorPage() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [selection, setSelection] = useState({ base: null, options: [], installments: 1 });
    const [clientInfo, setClientInfo] = useState({ company: '', name: '', email: '', phone: '' });
    const [loading, setLoading] = useState(false);

    // --- LOGIC ---
    const toggleOption = (opt) => {
        const exists = selection.options.find(o => o.id === opt.id);
        if (exists) {
            setSelection(p => ({ ...p, options: p.options.filter(o => o.id !== opt.id) }));
        } else {
            setSelection(p => ({ ...p, options: [...p.options, opt] }));
        }
    };

    const handleBaseSelect = (b) => {
        setSelection({ ...selection, base: b });
        // Auto-advance for smoother experience
        setTimeout(() => setStep(2), 200);
    };

    const total = (selection.base?.price || 0) + selection.options.reduce((acc, o) => acc + o.price, 0);

    const handleFinalize = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // 1. Create Customer
            let customerId;
            try {
                const newC = await pb.collection('customers').create({ ...clientInfo, created_by: user.id, commercial_id: user.id });
                customerId = newC.id;
            } catch {
                const exist = await pb.collection('customers').getFirstListItem(`email="${clientInfo.email}"`);
                customerId = exist.id;
            }

            // 2. Create Quote
            const quoteData = {
                title: `Projet ${selection.base.title} - ${clientInfo.company}`,
                customer_id: customerId,
                status: 'draft',
                total: total,
                items: [selection.base, ...selection.options].map(i => ({ description: i.title, quantity: 1, unit_price: i.price, total: i.price })),
                created_by: user.id
            };
            const quote = await pb.collection('quotes').create(quoteData);

            // 3. Contract
            await pb.collection('contracts').create({
                title: `Contrat - ${clientInfo.company}`,
                customer: customerId,
                quote: quote.id,
                status: 'draft',
                content: `<p>Contrat généré.</p>`,
                terms_accepted: false
            });

            setStep(5);
        } catch (err) {
            console.error(err);
            alert("Erreur: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ height: '100vh', width: '100vw', background: '#0f172a', color: 'white', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif' }}>
            {/* Header Minimalist */}
            <div style={{ padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.2)', flexShrink: 0 }}>
                <div style={{ fontWeight: 800, fontSize: '1.2rem', letterSpacing: '1px' }}>CONFIGURATEUR</div>
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.8rem', opacity: 0.6 }}>TOTAL PROJET</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#4ade80' }}>{total} € <span style={{ fontSize: '0.9rem', color: 'white', opacity: 0.5 }}>HT</span></div>
                    </div>
                    <button onClick={() => navigate('/commercial/launchpad')} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: 40, height: 40, borderRadius: '50%', cursor: 'pointer', fontSize: '1.2rem' }}>✕</button>
                </div>
            </div>

            {/* Main Content Area - SCROLLABLE */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 40, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                {/* Visual Progress Steps */}
                {step < 5 && (
                    <div style={{ display: 'flex', gap: 12, marginBottom: 40, flexShrink: 0 }}>
                        {[1, 2, 3, 4].map(s => (
                            <div key={s} style={{ width: 60, height: 4, borderRadius: 2, background: step >= s ? (step === s ? '#60a5fa' : '#3b82f6') : 'rgba(255,255,255,0.2)', transition: 'all 0.3s' }} />
                        ))}
                    </div>
                )}

                {step === 1 && (
                    <div className="animate-fade-in" style={{ width: '100%', maxWidth: 1200, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <h1 style={{ textAlign: 'center', fontSize: '3rem', marginBottom: 60 }}>Quel est le socle du projet ? 🏗️</h1>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 40 }}>
                            {BASES.map(b => (
                                <div
                                    key={b.id}
                                    onClick={() => handleBaseSelect(b)}
                                    style={{
                                        background: selection.base?.id === b.id ? 'linear-gradient(135deg, #1e40af, #1e3a8a)' : 'rgba(255,255,255,0.05)',
                                        border: selection.base?.id === b.id ? '2px solid #60a5fa' : '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: 24, padding: 40, cursor: 'pointer', transition: 'all 0.3s',
                                        transform: selection.base?.id === b.id ? 'scale(1.02)' : 'scale(1)',
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'
                                    }}
                                >
                                    <div style={{ fontSize: '4rem', marginBottom: 20 }}>{b.emoji}</div>
                                    <h2 style={{ fontSize: '1.8rem', marginBottom: 12 }}>{b.title}</h2>
                                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#93c5fd', marginBottom: 20 }}>{b.price} €</div>
                                    <p style={{ opacity: 0.7, lineHeight: 1.6, fontSize: '1.1rem' }}>{b.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="animate-fade-in" style={{ width: '100%', maxWidth: 1200, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <h1 style={{ textAlign: 'center', fontSize: '3rem', marginBottom: 60 }}>Ajoutons de la valeur ✨</h1>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 30 }}>
                            {OPTIONS.map(opt => {
                                const selected = selection.options.find(o => o.id === opt.id);
                                return (
                                    <div
                                        key={opt.id}
                                        onClick={() => toggleOption(opt)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 24, padding: 30,
                                            background: selected ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255,255,255,0.05)',
                                            border: selected ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: 20, cursor: 'pointer', transition: 'all 0.2s'
                                        }}
                                    >
                                        <div style={{ fontSize: '3rem' }}>{opt.emoji}</div>
                                        <div style={{ flex: 1 }}>
                                            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.4rem' }}>{opt.title}</h3>
                                            <p style={{ margin: 0, opacity: 0.6 }}>{opt.desc}</p>
                                        </div>
                                        <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>+{opt.price} €</div>
                                        <div style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid white', background: selected ? '#3b82f6' : 'transparent', display: 'grid', placeItems: 'center' }}>
                                            {selected && <span style={{ fontSize: '0.8rem' }}>✓</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="animate-fade-in" style={{ width: '100%', maxWidth: 800, textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <h1 style={{ fontSize: '3rem', marginBottom: 60 }}>Facilité de paiement 💳</h1>
                        <p style={{ fontSize: '1.2rem', opacity: 0.8, marginBottom: 40 }}>Comment souhaitez-vous régler cet investissement de {total} € ?</p>

                        <div style={{ display: 'flex', flexDirection: 'row', gap: 20, flexWrap: 'wrap', justifyItems: 'center', justifyContent: 'center' }}>
                            {[1, 3, 10].map(n => (
                                <div
                                    key={n}
                                    onClick={() => setSelection({ ...selection, installments: n })}
                                    style={{
                                        flex: '1 0 200px', padding: 40, background: selection.installments === n ? 'white' : 'rgba(255,255,255,0.05)',
                                        color: selection.installments === n ? 'black' : 'white',
                                        borderRadius: 24, cursor: 'pointer', transition: 'all 0.3s'
                                    }}
                                >
                                    <div style={{ fontSize: '2rem', fontWeight: 800, marginBottom: 10 }}>{n}x</div>
                                    <div style={{ fontSize: '1.1rem' }}>{Math.ceil(total / n)} € / mois</div>
                                    {n > 1 && <div style={{ fontSize: '0.8rem', marginTop: 10, opacity: 0.6 }}>Sans frais</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {step === 4 && (
                    <div className="animate-fade-in" style={{ width: '100%', maxWidth: 600, marginTop: 40, marginBottom: 40 }}>
                        <h1 style={{ textAlign: 'center', fontSize: '2.5rem', marginBottom: 40 }}>Finalisation du dossier 🤝</h1>
                        <form onSubmit={handleFinalize} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                            <input required placeholder="Nom de l'Entreprise" value={clientInfo.company} onChange={e => setClientInfo({ ...clientInfo, company: e.target.value })} style={inputStyle} />
                            <input required placeholder="Nom du Contact" value={clientInfo.name} onChange={e => setClientInfo({ ...clientInfo, name: e.target.value })} style={inputStyle} />
                            <input required placeholder="Email pour réception du dossier" type="email" value={clientInfo.email} onChange={e => setClientInfo({ ...clientInfo, email: e.target.value })} style={inputStyle} />
                            <input required placeholder="Numéro de téléphone" value={clientInfo.phone} onChange={e => setClientInfo({ ...clientInfo, phone: e.target.value })} style={inputStyle} />

                            <button type="submit" disabled={loading} style={{ marginTop: 20, padding: 24, background: '#10b981', color: 'white', border: 'none', borderRadius: 16, fontSize: '1.4rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 10px 30px rgba(16, 185, 129, 0.4)' }}>
                                {loading ? 'Création en cours...' : 'Valider & Signer le Devis'}
                            </button>
                        </form>
                    </div>
                )}

                {step === 5 && (
                    <div className="animate-fade-in" style={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <div style={{ fontSize: '6rem', marginBottom: 20 }}>🎉</div>
                        <h1 style={{ fontSize: '3rem', marginBottom: 20 }}>Félicitations !</h1>
                        <p style={{ fontSize: '1.5rem', opacity: 0.8, maxWidth: 600, margin: '0 auto 40px auto' }}>
                            Le dossier est créé. Votre client a reçu ses accès.
                            <br />Merci pour cette vente !
                        </p>
                        <button onClick={() => navigate('/commercial/launchpad')} style={{ padding: '16px 32px', background: 'white', color: 'black', border: 'none', borderRadius: 50, fontSize: '1.1rem', fontWeight: 700, cursor: 'pointer', alignSelf: 'center' }}>
                            Retour au Quartier Général
                        </button>
                    </div>
                )}

                {/* Footer Navigation Checks */}
                {step < 5 && (
                    <div style={{ width: '100%', maxWidth: 1200, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', flexShrink: 0, paddingBottom: 20 }}>
                        {step > 1 ? (
                            <button onClick={() => setStep(step - 1)} style={{ background: 'transparent', border: 'none', color: 'white', opacity: 0.6, fontSize: '1.1rem', cursor: 'pointer' }}>← Retour</button>
                        ) : <div />}

                        {step < 4 && (
                            <button
                                onClick={() => setStep(step + 1)}
                                disabled={step === 1 && !selection.base}
                                style={{
                                    padding: '16px 40px', background: step === 1 && !selection.base ? '#334155' : 'white',
                                    color: step === 1 && !selection.base ? '#94a3b8' : 'black',
                                    borderRadius: 50, fontSize: '1.2rem', fontWeight: 700, border: 'none', cursor: 'pointer',
                                    opacity: step === 1 && !selection.base ? 0.5 : 1, transition: 'all 0.3s'
                                }}
                            >
                                Continuer →
                            </button>
                        )}
                    </div>
                )}
            </div>

            <style>{`
                ::placeholder { color: rgba(255,255,255,0.4); }
                .animate-fade-in { animation: fadeIn 0.5s ease-out; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
}

const inputStyle = {
    padding: 20, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 12, color: 'white', fontSize: '1.1rem', outline: 'none'
};
